import { useState, useEffect } from 'react';
import { useLocation, Link } from 'wouter';
import { useAuthStore } from '../../hooks/useAuth';
import { isOperationsTierUser } from '../../utils/entitlements';
import { isLegalDocsPublished } from '../../config/legalConfig';
import { useDelayAlertStore } from '../../hooks/useDelayAlertStore';
import { useAgentBadge } from '../../hooks/useAgentBadge';
import { useLogisticsIntel } from '../../hooks/useLogisticsIntel';
import { useSystemConfig } from '../../lib/system-config';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Home,
    Calendar,
    Anchor,
    BarChart2,
    Settings,
    LogOut,
    ChevronRight,
    ChevronLeft,
    Sparkles,
    CloudRain,
    Sun,
    CloudLightning,
    Activity,
    Shield,
    MapPin,
    PackageSearch,
    Clock,
    Truck,
    Users,
    Mail,
    TrendingUp,
    X,
    Layers,
    Leaf,
    Plus,
    AlertTriangle
} from 'lucide-react';

export function Sidebar({ mobileOpen = false, onMobileClose }: { mobileOpen?: boolean; onMobileClose?: () => void }) {
    const [location, setLocation] = useLocation();
    const { user, logout, setLocation: setAuthLocation } = useAuthStore();
    const { activeAlerts } = useDelayAlertStore();
    const agentBadge = useAgentBadge();

    // Strict Intelligence Hook
    const { status, weather, transit } = useLogisticsIntel();

    const [isCollapsed, setIsCollapsed] = useState(false);
    const [signingOut, setSigningOut] = useState(false);

    const handleSignOut = async () => {
        if (signingOut) return;
        const redirectPath = user?.role === 'carrier' ? '/carrier-login' : '/login';
        setSigningOut(true);
        try {
            const ok = await logout();
            if (ok) {
                setLocation(redirectPath);
            }
        } finally {
            setSigningOut(false);
        }
    };

    // Detention Logic Sync: Check for high severity alerts to trigger dock pulse
    const hasDetentionAlert = activeAlerts.some(a => a.severity === 'CRITICAL');

    useEffect(() => {
        const saved = localStorage.getItem('sidebar_collapsed');
        if (saved) setIsCollapsed(saved === 'true');
    }, []);

    const toggleCollapse = () => {
        const newState = !isCollapsed;
        setIsCollapsed(newState);
        localStorage.setItem('sidebar_collapsed', String(newState));
    };

    const handleEnableLocation = () => {
        if ('geolocation' in navigator) {
            navigator.geolocation.getCurrentPosition((pos) => {
                setAuthLocation(pos.coords.latitude, pos.coords.longitude);
                useSystemConfig.getState().updateSection('facility', {
                    latitude: String(pos.coords.latitude),
                    longitude: String(pos.coords.longitude),
                });
            });
        }
    };

    // Navigation Groups
    interface NavItem {
        path: string;
        label: string;
        icon: any;
        role: string;
        badge?: string;
        pulse?: boolean;
        action?: () => void;
    }

    const isCarrier = user?.role === 'carrier';

    // Badge: action-required count takes priority, otherwise show recent auto-approved
    const agentBadgeText = agentBadge.count > 0
        ? String(agentBadge.count)
        : agentBadge.recentAutoApproved > 0
            ? String(agentBadge.recentAutoApproved)
            : undefined;

    const navGroups: { title: string; items: NavItem[] }[] = isCarrier
        ? [
            {
                title: 'MENU',
                items: [
                    { path: '/carrier-dashboard', label: 'Your Appointments', icon: Home, role: 'carrier' },
                    { path: '/carrier-request', label: 'Request Appointment', icon: Plus, role: 'carrier' },
                    { path: '/carrier-settings', label: 'Settings', icon: Settings, role: 'carrier' },
                ]
            }
        ]
        : [
        {
            title: 'OPERATIONS',
            items: [
                { path: '/dashboard', label: 'Operations Overview', icon: Home, role: 'all' },
                { path: '/inbox', label: 'Inbox', icon: Mail, role: 'warehouse_manager' },
                { path: '/appointments', label: 'Schedule', icon: Calendar, role: 'all' },
                { path: '/docks', label: 'Dock Status', icon: Anchor, role: 'warehouse_manager', pulse: hasDetentionAlert },
            ]
        },
        {
            title: 'AI DOCK COORDINATOR',
            items: [
                { path: '/agent/inbox', label: 'Agent Inbox', icon: Sparkles, role: 'warehouse_manager', badge: agentBadgeText },
                { path: '/agent/activity', label: 'Activity Log', icon: Activity, role: 'warehouse_manager' },
                { path: '/agent/rules', label: 'Agent Rules', icon: Shield, role: 'warehouse_manager' },
            ]
        },
        {
            title: 'INBOUND',
            items: [
                { path: '/orders', label: 'Purchase Orders', icon: PackageSearch, role: 'all' },
                { path: '/loads', label: 'Loads', icon: Layers, role: 'warehouse_manager' },
            ]
        },
        {
            title: 'OUTBOUND',
            items: [
                { path: '/sales-orders', label: 'Sales Orders', icon: Truck, role: 'all' },
            ]
        },
        {
            title: 'PERFORMANCE',
            items: [
                { path: '/analytics', label: 'Efficiency Report', icon: BarChart2, role: 'warehouse_manager' },
                { path: '/dwell-analytics', label: 'Dwell Times', icon: Clock, role: 'warehouse_manager' },
                { path: '/reports/environmental-impact', label: 'Environmental Impact', icon: Leaf, role: 'warehouse_manager' },
                { path: '/reports/receiving-exceptions', label: 'Receiving Exceptions', icon: AlertTriangle, role: 'warehouse_manager' },
                { path: '/ai-insights', label: 'AI Insights', icon: TrendingUp, role: 'warehouse_manager' },
            ]
        },
        {
            title: 'SYSTEM',
            items: [
                { path: '/facilities', label: 'Facilities', icon: MapPin, role: 'warehouse_manager' },
                { path: '/carriers', label: 'Carriers', icon: Truck, role: 'warehouse_manager' },
                { path: '/customers', label: 'Customers', icon: Users, role: 'warehouse_manager' },
                { path: '/settings', label: 'Settings', icon: Settings, role: 'warehouse_manager' },
                { path: '/admin', label: 'Admin', icon: Shield, role: 'admin' },
            ]
        }
    ];

    const containerVariants = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
                delayChildren: 0.2
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, x: -10 },
        show: { opacity: 1, x: 0 }
    };

    // Shared inner content rendered by both desktop and mobile sidebars
    const sidebarContent = (
        <>
            {/* Branding Area */}
            <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-start px-5'} h-16 border-b border-border`}>
                <div className="p-2 bg-primary/10 rounded-lg">
                    <span className="text-xl">🚢</span>
                </div>

                <AnimatePresence>
                    {!isCollapsed && (
                        <motion.div
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -10 }}
                            className="ml-3 min-w-0"
                        >
                            <h1 className="text-base font-semibold text-foreground">BayWhiz</h1>
                            <div className="flex items-center gap-1.5">
                                <span className="text-[10px] text-muted-foreground uppercase tracking-wider truncate">
                                    {isCarrier ? 'Carrier Portal' : ((user as { warehouseName?: string } | null)?.warehouseName || 'Dock Operations')}
                                </span>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Mobile close button */}
                {onMobileClose && (
                    <button
                        onClick={onMobileClose}
                        className="ml-auto p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-black/10 dark:hover:bg-white/10 transition-colors md:hidden"
                        aria-label="Close menu"
                    >
                        <X size={18} />
                    </button>
                )}
            </div>

            {/* Smart Actions (AI Suggestions) - Simplified (Operations tier only) */}
            <AnimatePresence>
                {!isCollapsed && user?.role === 'warehouse_manager' && isOperationsTierUser(user) && activeAlerts.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="px-4 pt-4 pb-2"
                    >
                        <div className="flex items-center justify-between mb-2 px-1">
                            <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                                <Sparkles size={10} className="text-primary" /> Alerts
                            </span>
                        </div>
                        <div className="space-y-1.5">
                            {activeAlerts.slice(0, 3).map((alert) => (
                                <div
                                    key={alert.id}
                                    className="w-full text-left p-2.5 rounded-lg bg-primary/5 border border-primary/10"
                                >
                                    <div className="flex items-start gap-2">
                                        <div className="p-1 bg-primary/10 rounded text-primary">
                                            <Anchor size={12} />
                                        </div>
                                        <div>
                                            <p className="text-xs font-medium text-foreground">{alert.carrier}</p>
                                            <p className="text-[10px] text-muted-foreground">{alert.minutesLate}m late</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Navigation Items */}
            <nav className="flex-1 py-4 px-3 space-y-6 overflow-y-auto custom-scrollbar">
                {navGroups.map((group) => {
                    const filteredItems = group.items.filter(item => {
                        if (item.role === 'all') return true;
                        if (item.role === 'admin' && user?.isAdmin) return true;
                        if (user?.role === 'admin') return true; // Admin role sees everything
                        return user?.role === item.role;
                    });

                    if (filteredItems.length === 0) return null;

                    return (
                        <motion.div
                            key={group.title}
                            variants={containerVariants}
                            initial="hidden"
                            animate="show"
                        >
                            {!isCollapsed && (
                                <h3 className="px-4 mb-2 text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
                                    {group.title}
                                </h3>
                            )}

                            <div className="space-y-1">
                                {filteredItems.map((item) => {
                                    const isActive = location === item.path;
                                    const isPulsing = item.pulse;

                                    return (
                                        <motion.div variants={itemVariants} key={item.path} className="relative group">
                                            {isActive && (
                                                <motion.div
                                                    layoutId="activeTab"
                                                    className="absolute inset-0 bg-black/5 dark:bg-white/5 rounded-xl border border-black/5 dark:border-white/5"
                                                    initial={false}
                                                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                                                />
                                            )}
                                            {isActive && (
                                                <motion.div
                                                    layoutId="activeIndicator"
                                                    className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-primary rounded-r-full shadow-[0_0_12px_rgba(8,145,178,0.8)]"
                                                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                                                />
                                            )}

                                            {/* Logic Split: Button vs Link */}
                                            {(item as any).action ? (
                                                <button
                                                    onClick={() => {
                                                        (item as any).action();
                                                    }}
                                                    className={`
                                                        relative w-full flex items-center ${isCollapsed ? 'justify-center' : 'px-4'} py-3 rounded-xl transition-all duration-200
                                                        text-muted-foreground hover:text-foreground hover:bg-black/5 dark:hover:bg-white/5
                                                        focus:outline-none focus:ring-1 focus:ring-primary/50 group/btn
                                                    `}
                                                >
                                                    <motion.div
                                                        className={`
                                                            relative z-10 p-1 rounded-md transition-colors 
                                                            group-hover/btn:bg-black/10 dark:group-hover/btn:bg-white/10 group-hover/btn:text-foreground
                                                        `}
                                                        whileHover={{ scale: 1.1, rotate: isCollapsed ? 0 : 5 }}
                                                        style={{ transformStyle: 'preserve-3d' }}
                                                    >
                                                        <item.icon size={18} />
                                                    </motion.div>
                                                    {!isCollapsed && (
                                                        <span className="ml-3 font-outfit font-medium text-sm tracking-wide z-10">{item.label}</span>
                                                    )}
                                                </button>
                                            ) : (
                                                <Link
                                                    href={item.path}
                                                    className={`
                                                        relative w-full flex items-center ${isCollapsed ? 'justify-center' : 'px-4'} py-3 rounded-xl transition-all duration-200
                                                        ${isActive ? 'text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-black/5 dark:hover:bg-white/5'}
                                                        focus:outline-none focus:ring-1 focus:ring-primary/50 group/btn
                                                    `}
                                                >
                                                    <motion.div
                                                        className={`
                                                            relative z-10 p-1 rounded-md transition-colors 
                                                            ${isActive ? 'bg-primary/20 text-primary' : 'group-hover/btn:bg-black/10 dark:group-hover/btn:bg-white/10 group-hover/btn:text-foreground'}
                                                            ${isPulsing ? 'animate-pulse-glow shadow-[0_0_15px_rgba(8,145,178,0.5)] text-primary border border-primary/50' : ''}
                                                        `}
                                                        whileHover={{ scale: 1.1, rotate: isCollapsed ? 0 : 5 }}
                                                        style={{ transformStyle: 'preserve-3d' }}
                                                    >
                                                        {/* Thematic Icon Animations */}
                                                        {item.label === 'Docks' ? (
                                                            <motion.div
                                                                animate={isActive || isPulsing ? { rotate: [0, -10, 10, 0] } : {}}
                                                                transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                                                            >
                                                                <item.icon size={18} />
                                                            </motion.div>
                                                        ) : (
                                                            <item.icon size={18} />
                                                        )}
                                                    </motion.div>

                                                    {!isCollapsed && (
                                                        <>
                                                            <span className="ml-3 flex-1 font-outfit font-medium text-sm tracking-wide z-10">{item.label}</span>
                                                            {item.badge && (
                                                                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold font-inter shadow-sm z-10 bg-cyan-100 dark:bg-cyan-900/50 text-slate-900 dark:text-cyan-100 border border-cyan-300 dark:border-cyan-700">
                                                                    {item.badge}
                                                                </span>
                                                            )}
                                                        </>
                                                    )}

                                                    {/* Collapsed: show dot badge indicator */}
                                                    {isCollapsed && item.badge && (
                                                        <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 rounded-full z-20 bg-cyan-400 shadow-[0_0_6px_rgba(34,211,238,0.6)]" />
                                                    )}

                                                    {/* Tooltip for Collapsed State */}
                                                    {isCollapsed && (
                                                        <div className="absolute left-full ml-4 px-3 py-1.5 bg-card border border-border rounded-lg text-xs font-inter text-foreground opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-50 shadow-xl backdrop-blur-md translate-x-2 group-hover:translate-x-0">
                                                            {item.label}{item.badge ? ` (${item.badge})` : ''}
                                                        </div>
                                                    )}
                                                </Link>
                                            )}
                                        </motion.div>
                                    );
                                })}
                            </div>
                        </motion.div>
                    );
                })}
            </nav>

            {/* Logistics Intel Mini-Widget */}
            <AnimatePresence>
                {!isCollapsed && user?.role === 'warehouse_manager' && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, height: 0 }}
                        className="px-4 pb-4 mt-auto"
                    >
                        <div className="p-3 rounded-lg bg-card border border-border">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-[9px] uppercase font-medium text-muted-foreground tracking-wider">Intel</span>
                                <span className={`w-1.5 h-1.5 rounded-full ${status === 'ACTIVE' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                            </div>

                            {status === 'LOCATION_REQUIRED' ? (
                                <button
                                    onClick={handleEnableLocation}
                                    className="w-full flex items-center justify-between text-left group/btn"
                                >
                                    <div className="flex items-center gap-2">
                                        <MapPin size={12} className="text-primary" />
                                        <span className="text-[11px] text-muted-foreground group-hover/btn:text-foreground transition-colors">
                                            Enable
                                        </span>
                                    </div>
                                    <ChevronRight size={12} className="text-muted-foreground group-hover/btn:text-foreground" />
                                </button>
                            ) : (
                                <div className="space-y-1.5">
                                    <div className="flex items-center gap-2">
                                        {weather?.isStorm ? (
                                            <CloudLightning size={12} className="text-red-400 animate-pulse" />
                                        ) : weather?.condition?.toLowerCase().includes('rain') ? (
                                            <CloudRain size={12} className="text-blue-400" />
                                        ) : (
                                            <Sun size={12} className="text-amber-400" />
                                        )}
                                        <span className="text-[11px] text-muted-foreground">
                                            Temp: <strong className={weather?.isStorm ? 'text-red-400' : 'text-foreground'}>
                                                {weather ? `${weather.temperature}°F` : '...'}
                                            </strong>
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Activity size={12} className="text-slate-400" />
                                        <span className="text-[11px] text-muted-foreground">
                                            Traffic: <strong className={transit?.impactLevel !== 'NONE' ? 'text-red-400' : 'text-emerald-400'}>
                                                {transit?.impactLevel !== 'NONE' ? 'Delays' : 'Good'}
                                            </strong>
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Collapse Toggle */}
            <button
                onClick={toggleCollapse}
                className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-card border border-border rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors z-50"
            >
                {isCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
            </button>

            {/* Profile Footer */}
            <div className="p-4 border-t border-border">
                <div className={`flex items-center ${isCollapsed ? 'flex-col gap-2 justify-center' : 'gap-3'}`}>
                    <motion.div
                        whileHover={{ scale: 1.05 }}
                        className="w-9 h-9 rounded-lg bg-gradient-to-br from-secondary to-card border border-border flex items-center justify-center relative overflow-hidden shadow-lg cursor-pointer"
                    >
                        <span className="font-bold text-xs text-foreground z-10">{user?.email?.[0]?.toUpperCase()}</span>
                        <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 to-transparent opacity-50" />
                    </motion.div>

                    {!isCollapsed && (
                        <div className="flex-1 min-w-0 overflow-hidden">
                            <h4 className="text-sm font-semibold text-foreground truncate font-outfit">{user?.email?.split('@')[0]}</h4>
                            <p className="text-[10px] text-muted-foreground truncate uppercase tracking-wider">{user?.role?.replace('_', ' ')}</p>
                        </div>
                    )}

                    <button
                        type="button"
                        onClick={handleSignOut}
                        disabled={signingOut}
                        aria-label="Sign out"
                        title="Sign out"
                        className="text-muted-foreground hover:text-red-400 transition-colors p-1 disabled:opacity-50"
                    >
                        <LogOut size={16} />
                    </button>
                </div>
                {!isCollapsed && isLegalDocsPublished() && (
                    <div
                        data-testid="sidebar-legal-footer"
                        className="mt-3 pt-2 border-t border-border/60 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[10px] text-muted-foreground"
                    >
                        <Link href="/terms" className="hover:text-foreground hover:underline">
                            Terms of Service
                        </Link>
                        <span aria-hidden="true">·</span>
                        <Link href="/privacy" className="hover:text-foreground hover:underline">
                            Privacy Policy
                        </Link>
                        <span aria-hidden="true">·</span>
                        <a href="mailto:support@baywhiz.com" className="hover:text-foreground hover:underline">
                            support@baywhiz.com
                        </a>
                    </div>
                )}
            </div>
        </>
    );

    return (
        <>
            {/* Desktop sidebar: flex layout, hidden on mobile */}
            <motion.aside
                initial={{ width: 256 }}
                animate={{ width: isCollapsed ? 80 : 256 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                className="relative h-screen hidden md:flex flex-col border-r border-border bg-gray-100 dark:bg-background z-50"
            >
                {sidebarContent}
            </motion.aside>

            {/* Mobile sidebar: fixed overlay, hidden on md+ */}
            <aside
                className={`fixed top-0 left-0 h-screen w-64 flex flex-col border-r border-border bg-gray-100 dark:bg-background z-50 md:hidden transition-transform duration-300 ease-in-out ${mobileOpen ? 'translate-x-0' : '-translate-x-full'
                    }`}
            >
                {sidebarContent}
            </aside>
        </>
    );
}
